import PropTypes from "prop-types";
import * as React from "react";
import {
  CheckIcon,
  CloseIcon,
  IconButton,
  PencilIcon,
  Spinner,
  Tooltip,
} from "../../themeV2";
import s from "./PurchasedServices.module.css";

// ── State machine ───────────────────────────────────────────────────────────
// status: 'idle' | 'editing' | 'saving' | 'saved' | 'error'

export default function EditableCustomerText({ service, onUpdateText }) {
  const [status, setStatus] = React.useState("idle");
  const [value, setValue] = React.useState(service.customerText ?? "");
  const editButtonRef = React.useRef(null);

  // Resync when the row is refreshed from the server while we are not editing.
  // Without this the editor reopens showing a stale draft.
  React.useEffect(() => {
    if (status === "idle") {
      setValue(service.customerText ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.customerText]);

  React.useEffect(() => {
    if (status !== "saved") return;
    const id = setTimeout(() => setStatus("idle"), 2000);
    return () => clearTimeout(id);
  }, [status]);

  const returnFocus = () => {
    requestAnimationFrame(() => editButtonRef.current?.focus());
  };

  const handleSave = async () => {
    const trimmed = value.trim();
    if (trimmed === (service.customerText ?? "")) {
      setStatus("idle");
      returnFocus();
      return;
    }
    setStatus("saving");
    try {
      await onUpdateText(service.purchaseServiceId, trimmed);
      setStatus("saved");
      returnFocus();
    } catch {
      setStatus("error");
    }
  };

  const handleCancel = () => {
    setValue(service.customerText ?? "");
    setStatus("idle");
    returnFocus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleBlur = (e) => {
    // Focus moving within the edit row (textarea → Save button) is not leaving.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    // Leaving discards, matching Escape. Committing here would silently save a
    // talent-visible field on a stray click.
    handleCancel();
  };

  const isEditing = status === "editing" || status === "saving";

  if (isEditing) {
    return (
      <div onBlur={handleBlur}>
        <div className={s.editRow}>
          <textarea
            className={s.inlineInput}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            autoFocus
            aria-label={`Edit text for ${service.label}`}
          />
          {/* Several services can be edited on one order, so the controls need
              names that say which one they act on. */}
          <IconButton
            size="sm"
            variant="text"
            onClick={handleSave}
            disabled={status === "saving"}
            label={`Save text for ${service.label}`}
          >
            {status === "saving" ? <Spinner size="sm" /> : <CheckIcon />}
          </IconButton>
          <IconButton
            size="sm"
            variant="text"
            onClick={handleCancel}
            disabled={status === "saving"}
            label={`Cancel editing ${service.label}`}
          >
            <CloseIcon />
          </IconButton>
        </div>
        {/* role="status" already implies aria-live="polite"; declaring
            assertive on top of it is contradictory. */}
        <div className={s.feedbackRow} role="status" aria-atomic="true">
          {status === "saving" && (
            <span className={s.savingFeedback}>Saving...</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={s.editRow}>
        <p className={s.customerText} style={{ margin: 0 }}>
          {service.customerText || "No text provided"}
        </p>
        <Tooltip.Root>
          <Tooltip.Trigger
            render={
              <button
                ref={editButtonRef}
                type="button"
                className={s.editTrigger}
                onClick={() => setStatus("editing")}
                aria-label={`Edit text for ${service.label}`}
              >
                <PencilIcon />
              </button>
            }
          />
          <Tooltip.Popup>{`Edit text for ${service.label}`}</Tooltip.Popup>
        </Tooltip.Root>
      </div>
      <div
        className={s.feedbackRow}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {status === "saved" && (
          <span className={`${s.successFeedback} ${s.fadeIn}`}>
            <CheckIcon />
            Saved
          </span>
        )}
        {status === "error" && (
          <span className={`${s.errorFeedback} ${s.fadeIn}`}>
            Failed to save
          </span>
        )}
      </div>
    </div>
  );
}

EditableCustomerText.propTypes = {
  service: PropTypes.shape({
    purchaseServiceId: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    customerText: PropTypes.string,
  }).isRequired,
  onUpdateText: PropTypes.func.isRequired,
};
